import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { isPast, addMinutes } from 'date-fns';
import * as jstz from 'jstz';

import * as rootReducer from '../store';
import { AuthService } from './auth.service';
import { API } from '../../shared/constants/app-service-constants';
import { Delivery } from '../models';

@Injectable({
  providedIn: 'root',
})
export class DeliveryService {
  prevChannelId;

  constructor(
    private http: HttpClient,
    private store: Store<rootReducer.State>,
    private authService: AuthService,
    private router: Router,
  ) {}

  getDeliveries(channelId: string, loadMore = false): Observable<Delivery[]> {
    const url = loadMore
      ? `${API.RECOMMENDATIONS}/${channelId}/loadMore`
      : `${API.RECOMMENDATIONS}/${channelId}`;
    this.prevChannelId = channelId;

    return this.http.get<any>(url).pipe(map((res) => res['deliveriesList']));
  }

  getDeliveryDetails(deliveryId: string): Observable<any> {
    const timezone = jstz.determine().name();
    return this.http.get(`${API.DELIVERY}/${deliveryId}`, { params: { timezone } });
  }

  getDeliveriesByType(type: 'favorite'): Observable<any> {
    const ids = JSON.parse(localStorage.getItem(type)) || [];
    return this.http.get<any>(`${API.TRACKS}`, { params: { ids: ids.join(',') } }).pipe(map((res) => res['tracks']));
  }

  getPrevChannelId() {
    return this.prevChannelId;
  }

  checkAccessForDelivery(): Observable<boolean> {
    return this.authService.isLoggined().pipe(
      tap((isLoginned: boolean) => {
        if (!isLoginned) {
          this.router.navigate(['/auth/login']);
        }
      }),
    );
  }

  setDeliveryItemsByType(id: string, type: string): void {
    const items = JSON.parse(localStorage.getItem(type)) || [];
    const index = items.findIndex((item: string) => item === id);

    index !== -1 ? items.splice(index, 1) : items.push(id);
    localStorage.setItem(type, JSON.stringify(items));
  }

  searchDeliveries(searchTerm: string): Observable<Delivery[]> {
    const params = { q: this.characterEscaping(searchTerm), type: 'track' };
    return this.http.get<any>(`${API.SEARCH}`, { params }).pipe(map((res) => res['dataList']));
  }

  characterEscaping(searchTerm: string): string {
    let newSearchTerm = searchTerm;
    const characters = [
      '$',
      '-',
      '_',
      '.',
      '+',
      '!',
      '*',
      '\'',
      '(',
      ')',
      ';',
      '/',
      '?',
      ':',
      '@',
      '=',
      '&',
      '"',
      '<',
      '>',
      '#',
      '%',
      '{',
      '}',
      '|',
      '^',
      '~',
      '[',
      ']',
      '`',
    ];
    characters.forEach((character: any) => {
      if (searchTerm.indexOf(character) !== -1) {
        newSearchTerm = searchTerm.replace(character, `\\${character}`);
      }
    });

    return newSearchTerm;
  }

  getDeliveryEmailText(deliveryId: string): Observable<string> {
    const timezone = jstz.determine().name();
    return this.http
      .get(`${API.DELIVERIES_ASSET}/${deliveryId}/emailText`, { params: { timezone } })
      .pipe(map((res: any) => res['emailText']));
  }

  isOfflineDeliveryExpired(delivery: Delivery): boolean {
    let { expiryDate } = delivery;

    if (!expiryDate) {
      return false;
    }

    expiryDate = expiryDate.replace(/-/gi, '/');
    const offsetDate = addMinutes(new Date(expiryDate), -1 * new Date().getTimezoneOffset());

    const isDateExpired = isPast(offsetDate);
    const isDownloaded = delivery.offlineStatus === 'downloaded';

    return isDateExpired && isDownloaded;
  }
}
